// apps/client/src/store/authStore.ts
//
// Authentication store using Zustand
//

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

interface User {
    id: string;
    telegramId: string;
    username: string | null;
    firstName: string;
    lastName?: string | null;
    chips: number;
    photoUrl?: string | null;
    isPremium?: boolean;
    isAdmin?: boolean;
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isAdmin: boolean;
    isLoading: boolean;
    error: string | null;

    // Actions
    login: (initData: string) => Promise<boolean>;
    logout: () => void;
    setUser: (user: User) => void;
    refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            isAdmin: false,
            isLoading: false,
            error: null,

            login: async (initData: string) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await api.login(initData);

                    if (response.ok && response.accessToken) {
                        api.setToken(response.accessToken);

                        set({
                            user: {
                                id: response.user.id,
                                telegramId: response.user.telegramId,
                                username: response.user.username,
                                firstName: response.user.firstName,
                                chips: response.user.chips,
                                isAdmin: (response as unknown as { isAdmin?: boolean }).isAdmin || false,
                            },
                            token: response.accessToken,
                            isAuthenticated: true,
                            isAdmin: (response as unknown as { isAdmin?: boolean }).isAdmin || false,
                            isLoading: false,
                        });

                        return true;
                    }

                    throw new Error('Login failed');
                } catch (error) {
                    const message = error instanceof Error ? error.message : 'Login failed';
                    set({ error: message, isLoading: false });
                    return false;
                }
            },

            logout: () => {
                api.clearToken();
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    isAdmin: false,
                    error: null,
                });
            },

            setUser: (user: User) => {
                set({ user });
            },

            refreshUser: async () => {
                const { token } = get();
                if (!token) return;

                try {
                    api.setToken(token);
                    const response = await api.getMe();

                    if (response.ok && response.user) {
                        set({ user: response.user as User });
                    }
                } catch (error) {
                    // Token might be invalid, logout
                    get().logout();
                }
            },
        }),
        {
            name: 'bingoton-auth',
            partialize: (state) => ({
                token: state.token,
                user: state.user,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);
